import type { AssetNote, SessionSearchAccess } from './types';

/**
 * Documento cru do índice, como o browser o recebe.
 *
 * Espelha `apps/surplus-api/src/services/product_search/documents.py`. Os nomes
 * divergem do `LotItem` de propósito — `in_stock` é booleano (a magnitude do
 * estoque fica fora do índice por decisão de exposição) e o tamanho tem dois
 * campos: `display_grade` é o que a UI mostra, `grade` é o valor cru
 * normalizado, presente só para filtro.
 *
 * Quase tudo é `optional` no schema e OMITIDO quando vazio, então tudo que não
 * é garantido pelo reindex entra aqui como opcional.
 */
export interface ProductSearchDocument {
  /** `_id` do Product — é o que `included_product_ids` guarda. */
  id: string;
  brand: string;
  /** Derivado no reindex: `brand|description|cor`, em maiúsculas. */
  model_key: string;
  has_photo: boolean;
  in_stock: boolean;
  suggested_sale_price: number;
  description?: string;
  category_display?: string;
  category_normalized?: string;
  product_code?: string;
  label_barcode?: string;
  color?: string;
  color_description?: string;
  grade?: string;
  display_grade?: string;
  product_silhouette?: string;
  season_normalized?: string;
  gender_normalized?: string;
  retail_price?: number;
  assets_urls?: string[];
  /** Legenda de procedência por foto, casada por URL. Ver `AssetNote`. */
  assets_notes?: AssetNote[];
  composition?: string;
  collection?: string;
  /**
   * Fornecedor de estoque próprio (pronta entrega). Sempre emitido pelo
   * reindex atual; opcional porque a coleção anterior ao primeiro ciclo depois
   * do deploy não tem o campo.
   */
  ready_delivery?: boolean;
}

export interface SearchGroup {
  group_key: string[];
  hits: { document: ProductSearchDocument }[];
}

export interface SearchFacetCount {
  field_name: string;
  counts: { value: string; count: number }[];
}

export interface ProductSearchResponse {
  /**
   * Com `group_by`, é a contagem de **grupos** — ou seja, de LINHAS, não de
   * SKUs. Quem ler isto como "peças no índice" anuncia 1462 onde há 719
   * linhas, e a paginação erra junto: `page` também conta grupos.
   */
  found: number;
  page: number;
  grouped_hits: SearchGroup[];
  facet_counts?: SearchFacetCount[];
}

/**
 * Falha do Typesense. `status === 401` é o caso que muda o texto na tela: a
 * scoped key é HMAC local, sem revogação, então 401 significa "a chave desta
 * sessão venceu — recarregue", nunca erro de rede.
 */
export class SearchError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'SearchError';
    this.status = status;
  }
}

/** Os mesmos campos de `SEARCHABLE_FIELDS` no schema do índice. */
const QUERY_BY = [
  'brand',
  'description',
  'category_display',
  'color_description',
  'product_code',
  'label_barcode',
].join(',');

/** Facetas que viram chips de filtro. `grade` cru NUNCA é facetado. */
export const FACET_BRAND = 'brand';
export const FACET_CATEGORY = 'category_normalized';
export const FACET_GRADE = 'display_grade';
/**
 * `color_description`, **nunca `color`**. O segundo é o código cru da planilha
 * (`PT`, `AZ-MRN`) e quem a UI exibe é o primeiro — o mesmo erro que o schema
 * do índice já documenta para tamanho.
 */
export const FACET_COLOR = 'color_description';
export const FACET_GENDER = 'gender_normalized';
export const FACET_SEASON = 'season_normalized';

/** As três facetas do self-service. A vitrine passa a sua própria lista. */
export const DEFAULT_FACET_BY = [
  FACET_BRAND,
  FACET_CATEGORY,
  FACET_GRADE,
].join(',');

/**
 * Ordenações admitidas — **só ascendente ou por relevância**, e isso é
 * restrição de contrato, não de UI.
 *
 * Com `group_limit=10`, um `suggested_sale_price:desc` devolve os 10 SKUs mais
 * CAROS de cada grupo, e `adaptGroup` calcula `Math.min` sobre eles: um modelo
 * de R$ 20 a R$ 90 anunciaria "a partir de R$ 35". Ordenar por maior preço
 * exige materializar o mínimo do modelo no reindex.
 */
export const SORT_PRICE_ASC = 'suggested_sale_price:asc';

/**
 * Quantos SKUs por modelo vêm em cada grupo.
 *
 * **Não é 1.** O agrupamento existe para dar uma linha por modelo, mas a linha
 * exibe um chip por grade disponível e o chip é o alvo de adição — e
 * `included_product_ids` guarda `product_id`, não modelo. Com `group_limit=1` a
 * linha só conheceria uma grade, e o cliente não teria como escolher o tamanho:
 * o agrupamento entregaria menos informação do que a lista sem agrupar.
 *
 * 10 cobre o pior grupo medido na base de dev com folga (o pior bucket depois
 * do agrupamento tinha 8 ocorrências). Um modelo com mais SKUs que isto mostra
 * as 10 primeiras grades — a alternativa seria pedir o grupo inteiro e pagar
 * banda por documento cru em todas as linhas.
 */
export const GROUP_LIMIT = 10;

export interface ProductSearchParams {
  q: string;
  /** Conta LINHAS (grupos), não SKUs. */
  page: number;
  perPage: number;
  /**
   * Cláusulas de **estreitamento** vindas dos chips. O escopo da sessão
   * (`brand:=[...] && in_stock:=true`) já vai embutido na chave e é combinado
   * com este por `&&` — não há como alargá-lo daqui.
   */
  filterBy?: string;
  /**
   * Campos facetados, separados por vírgula. Omitido, mantém as três facetas do
   * self-service — a vitrine pede seis.
   */
  facetBy?: string;
  /**
   * `sort_by` do Typesense. Omitido, vale a relevância do servidor.
   *
   * **Só ascendente.** Ver `SORT_PRICE_ASC`.
   */
  sortBy?: string;
  /**
   * Teto de valores por faceta. 12 cabe nos chips do self-service; a barra de
   * filtros da vitrine lista marca e cor, e 12 esconderia opção sem avisar.
   */
  maxFacetValues?: number;
  signal?: AbortSignal;
}

/**
 * Valor pronto para ir entre crases num `filter_by`.
 *
 * Mesma regra do backend (`scoped_keys.escape_brand_value`), e o motivo é o
 * mesmo: a vírgula separa itens da lista e a marca some da busca **sem erro
 * nenhum** — `found=0` e nada mais. A crase é removida em vez de escapada
 * porque o Typesense não documenta escape de crase dentro de crase; remover
 * erra para o lado do valor que não casa nada.
 */
export function quoteFilterValue(value: string): string {
  return '`' + value.replace(/`/g, '') + '`';
}

function baseUrl(access: SessionSearchAccess): string {
  return `${access.protocol}://${access.host}:${access.port}`;
}

export async function searchProducts(
  access: SessionSearchAccess,
  params: ProductSearchParams
): Promise<ProductSearchResponse> {
  const query = new URLSearchParams({
    q: params.q,
    query_by: QUERY_BY,
    // Uma linha por modelo (marca + descrição + cor); a grade vira escolha
    // DENTRO da linha.
    group_by: 'model_key',
    group_limit: String(GROUP_LIMIT),
    facet_by: params.facetBy ?? DEFAULT_FACET_BY,
    max_facet_values: String(params.maxFacetValues ?? 12),
    page: String(params.page),
    per_page: String(params.perPage),
  });
  if (params.filterBy) query.set('filter_by', params.filterBy);
  if (params.sortBy) query.set('sort_by', params.sortBy);

  const url = `${baseUrl(access)}/collections/${encodeURIComponent(
    access.collection
  )}/documents/search?${query.toString()}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: { 'X-TYPESENSE-API-KEY': access.api_key },
      signal: params.signal,
    });
  } catch (cause) {
    // Rede fora, CORS, serviço no chão: para a tela é tudo "busca
    // indisponível". Status 0 sinaliza que nem houve resposta.
    if (cause instanceof DOMException && cause.name === 'AbortError')
      throw cause;
    throw new SearchError('Busca indisponível', 0);
  }

  if (!response.ok) {
    throw new SearchError(
      response.status === 401 ? 'Busca expirada' : 'Busca indisponível',
      response.status
    );
  }

  const body = (await response.json()) as ProductSearchResponse;
  return {
    found: body.found ?? 0,
    page: body.page ?? params.page,
    // Sem `group_by` o Typesense devolveria `hits`; o campo só falta aqui se o
    // servidor recusar o agrupamento, e nesse caso lista vazia é melhor que
    // uma exceção de leitura de `undefined`.
    grouped_hits: body.grouped_hits ?? [],
    facet_counts: body.facet_counts,
  };
}
